import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const requestedPortal = searchParams.get("portal");
  const portal = ["admin", "it"].includes(requestedPortal)
    ? requestedPortal
    : "customer";
  const loginPath =
    portal === "admin"
      ? "/admin/login"
      : portal === "it"
        ? "/it/login"
        : "/login";
  const forgotPath = `/forgot-password?portal=${portal}`;
  const signInLabel =
    portal === "admin"
      ? "Admin Console"
      : portal === "it"
        ? "IT Service Desk"
        : "Customer";

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  const submit = async (event) => {
    event.preventDefault();

    if (!token) {
      toast.error("This password reset link is invalid.");
      return;
    }
    if (password.length < 8) {
      toast.error("Use a password with at least 8 characters.");
      return;
    }
    if (password !== confirmation) {
      toast.error("The passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setCompleted(true);
      toast.success("Your password has been reset.");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "This password reset link is invalid or has expired."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-lg border border-[#E7E5E4] bg-white p-8 shadow-sm">
          <div className="mb-8 text-center">
            <h1 className="font-serif text-2xl text-[#1C1917]">
              Reset Password
            </h1>
            <p className="mt-3 text-sm text-[#78716C]">
              Create a new secure password for your Nityavastra account.
            </p>
          </div>

          {!token ? (
            <div className="space-y-5 text-center">
              <p className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                This reset link is missing its security token.
              </p>
              <Link
                to={forgotPath}
                className="text-sm text-[#7C1F30] hover:underline"
              >
                Request a new reset link
              </Link>
            </div>
          ) : completed ? (
            <div className="space-y-5 text-center">
              <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                Your password has been reset successfully.
              </p>
              <Link
                to={loginPath}
                className="inline-block text-sm font-medium text-[#7C1F30] hover:underline"
              >
                Continue to {signInLabel} Sign In
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mt-2 bg-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-controls="new-password"
                  aria-pressed={showPassword}
                  aria-label={showPassword ? "Hide new password" : "Show new password"}
                  className="mt-2 inline-flex items-center gap-2 rounded text-xs text-[#7C1F30] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7C1F30]"
                >
                  {showPassword
                    ? <EyeOff className="h-4 w-4" aria-hidden="true" />
                    : <Eye className="h-4 w-4" aria-hidden="true" />}
                  {showPassword ? "Hide password" : "Show password"}
                </button>
              </div>

              <div>
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  type={showConfirmation ? "text" : "password"}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  className="mt-2 bg-white"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmation((visible) => !visible)}
                  aria-controls="confirm-password"
                  aria-pressed={showConfirmation}
                  aria-label={showConfirmation ? "Hide confirmed password" : "Show confirmed password"}
                  className="mt-2 inline-flex items-center gap-2 rounded text-xs text-[#7C1F30] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7C1F30]"
                >
                  {showConfirmation
                    ? <EyeOff className="h-4 w-4" aria-hidden="true" />
                    : <Eye className="h-4 w-4" aria-hidden="true" />}
                  {showConfirmation ? "Hide password" : "Show password"}
                </button>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-[#7C1F30] py-6 text-xs uppercase tracking-[0.2em] text-white hover:bg-[#8D2A3D]"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
